import { objectValues } from '../utils/fns';
import { NOTIFICATION_TYPES } from './type';
import { EventEmitter } from '../utils/event_emitter/event_emitter';
import { NOTIFICATION_LISTENER_EXCEPTION } from 'error_message';
import { ErrorReporter } from '../error/error_reporter';
/**
 * NotificationCenter allows registration and triggering of callback functions using
 * notification event types defined in NOTIFICATION_TYPES of utils/enums/index.js:
 * - ACTIVATE: An impression event will be sent to Optimizely.
 * - TRACK a conversion event will be sent to Optimizely
 */
export class DefaultNotificationCenter {
    /**
     * @constructor
     * @param   {NotificationCenterOptions}  options
     * @param   {LogHandler}                 options.logger       An instance of a logger to log messages with
     * @param   {ErrorHandler}               options.errorHandler An instance of errorHandler to handle any unexpected error
     */
    constructor(options) {
        this.removerId = 1;
        this.eventEmitter = new EventEmitter();
        this.removers = new Map();
        this.errorReporter = new ErrorReporter(options.logger, options.errorNotifier);
    }
    /**
     * Add a notification callback to the notification center
     * @param   {string}                   notificationType     One of the values from NOTIFICATION_TYPES in utils/enums/index.js
     * @param   {NotificationListener<T>}  callback             Function that will be called when the event is triggered
     * @returns {number}                   If the callback was successfully added, returns a listener ID which can be used
     * to remove the callback by calling removeNotificationListener. The ID is a number greater than 0.
     * If there was an error and the listener was not added, addNotificationListener returns -1. This
     * can happen if the first argument is not a valid notification type, or if the same callback
     * function was already added as a listener by a prior call to this function.
     */
    addNotificationListener(notificationType, callback) {
        const notificationTypeValues = objectValues(NOTIFICATION_TYPES);
        const isNotificationTypeValid = notificationTypeValues.indexOf(notificationType) > -1;
        if (!isNotificationTypeValid) {
            return -1;
        }
        const returnId = this.removerId++;
        const remover = this.eventEmitter.on(notificationType, this.wrapWithErrorReporting(notificationType, callback));
        this.removers.set(returnId, remover);
        return returnId;
    }
    wrapWithErrorReporting(notificationType, callback) {
        return (notificationData) => {
            try {
                callback(notificationData);
            }
            catch (ex) {
                const message = ex instanceof Error ? ex.message : String(ex);
                this.errorReporter.report(NOTIFICATION_LISTENER_EXCEPTION, notificationType, message);
            }
        };
    }
    /**
     * Remove a previously added notification callback
     * @param   {number}                 listenerId ID of listener to be removed
     * @returns {boolean}                Returns true if the listener was found and removed, and false
     * otherwise.
     */
    removeNotificationListener(listenerId) {
        const remover = this.removers.get(listenerId);
        if (remover) {
            remover();
            this.removers.delete(listenerId);
            return true;
        }
        return false;
    }
    /**
     * Removes all previously added notification listeners, for all notification types
     */
    clearAllNotificationListeners() {
        this.eventEmitter.removeAllListeners();
    }
    /**
     * Remove all previously added notification listeners for the argument type
     * @param   {NotificationType}    notificationType One of NotificationType
     */
    clearNotificationListeners(notificationType) {
        this.eventEmitter.removeListeners(notificationType);
    }
    /**
     * Fires notifications for the argument type. All registered callbacks for this type will be
     * called. The notificationData object will be passed on to callbacks called.
     * @param {NotificationType} notificationType One of NotificationType
     * @param {Object} notificationData Will be passed to callbacks called
     */
    sendNotifications(notificationType, notificationData) {
        this.eventEmitter.emit(notificationType, notificationData);
    }
}
/**
 * Create an instance of NotificationCenter
 * @param   {NotificationCenterOptions}   options
 * @returns {NotificationCenter}          An instance of NotificationCenter
 */
export function createNotificationCenter(options) {
    return new DefaultNotificationCenter(options);
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=index.js.map